import { restoreSession, refreshToken } from "./auth.js";
import { getToken, apiFetch } from "./api.js";
import { loadFeedInto, loadSettingsInto } from "./groups.js";
import { renderAbout } from "./about.js";
import { renderLanding } from "./landing.js";
import { maybeRenderEmailAsk } from "./email-ask.js";
import { botUsername, toast, resolveActiveGroup, escapeHtml, feedHash } from "./util.js";
import { t } from "./i18n.js";
import { metaLabel, sortGroups } from "./sidebar.js";
import {
  renderJoinPage,
  renderSetupClaim,
  renderSetupWizard,
} from "./setup-wizard.js";

type Page = "feed" | "settings" | "about" | "setup" | "new" | "correspondence-details";

/** Path-based entries that exist before the user has any groups (or any
 *  session): /join/<token> for invitees, /setup/<token> for a new founder.
 *  The server returns index.html for both, so the SPA routes on pathname. */
function pathRoute(): { kind: "join" | "claim"; token: string } | null {
  let m = /^\/join\/([\w~.-]+)/.exec(window.location.pathname);
  if (m) return { kind: "join", token: m[1] };
  m = /^\/setup\/([\w~.-]+)/.exec(window.location.pathname);
  if (m) return { kind: "claim", token: m[1] };
  return null;
}

interface AppState {
  client: any;
  me: { id: number; username?: string };
  page: Page;
  activeGroupId: string | null;
  /** First group, used as the fallback when the hash names no group. */
  defaultGroupId: string | null;
  botUsername: string;
}

/** The URL hash is the single source of truth for navigation:
 *   #about                  → about page
 *   #settings               → personal settings (email, delivery, logout)
 *   #g/<id>                 → that group's feed
 *   #g/<id>/settings        → that group's correspondence details
 * A bare/empty hash falls back to the active (or first) group's feed. Encoding
 * the group in the hash means refresh and deep-links restore where you were. */
function parseHash(): { page: Page; groupId: string | null } {
  const h = window.location.hash.slice(1);
  if (h === "about") return { page: "about", groupId: null };
  if (h === "new") return { page: "new", groupId: null };
  if (h === "settings") return { page: "settings", groupId: null };
  const s = /^setup\/([^/]+)$/.exec(h);
  if (s) return { page: "setup", groupId: s[1] };
  const m = /^g\/([^/]+)(?:\/(settings))?$/.exec(h);
  if (m) return { page: m[2] === "settings" ? "correspondence-details" : "feed", groupId: m[1] };
  return { page: "feed", groupId: null };
}

/** Render minimal top nav: Settings, About. "New correspondence" lives in the
 *  sidebar under the group list; logout lives on the Settings page. */
function renderNav(s: AppState): string {
  return `
    <a href="#settings" class="${s.page === "settings" ? "active" : ""}">${t("app.settings")}</a>
    <a href="#about" class="${s.page === "about" ? "active" : ""}">${t("app.about")}</a>
  `;
}

async function routeMain(s: AppState, page: Page): Promise<void> {
  s.page = page;
  document.getElementById("nav")!.innerHTML = renderNav(s);

  const main = document.getElementById("main")!;
  switch (page) {
    case "about":
      main.innerHTML = '<div id="about-view"></div>';
      return renderAbout(document.getElementById("about-view")!, s.botUsername);
    case "feed":
      if (!s.activeGroupId) {
        main.innerHTML = `<p class="feed-empty">${t("app.select")}</p>`;
        return;
      }
      main.innerHTML = `
        <div id="feed-view">
          <div class="feed-meta" id="feed-meta">Loading…</div>
          <div class="feed" id="feed-messages"><p class="feed-loading">Loading…</p></div>
          <div id="feed-settings-anchor"></div>
          <div id="compose-anchor"></div>
        </div>`;
      return loadFeedInto(s, s.activeGroupId);
    case "settings": {
      main.innerHTML = '<div id="settings-view">Loading…</div>';
      const { loadPersonalSettingsView } = await import("./settings-personal.js");
      return loadPersonalSettingsView(document.getElementById("settings-view")!);
    }
    case "correspondence-details":
      if (!s.activeGroupId) {
        main.innerHTML = `<p class="feed-empty">${t("app.select")}</p>`;
        return;
      }
      main.innerHTML = '<div id="settings-view">Loading…</div>';
      return loadSettingsInto(s, s.activeGroupId);
    case "setup":
      if (!s.activeGroupId) {
        main.innerHTML = `<p class="feed-empty">${t("app.select")}</p>`;
        return;
      }
      return renderSetupWizard(main, s.client, s.activeGroupId);
    case "new":
      main.innerHTML = '<div id="new-circle-view"></div>';
      return renderSetupClaim(document.getElementById("new-circle-view")!, s.client);
  }
}

/** Populate the sidebar with the user's groups. Clicking one opens its feed. */
async function loadSidebar(state: AppState): Promise<void> {
  const el = document.getElementById("sidebar-groups");
  if (!el) return;
  try {
    const { groups } = await apiFetch("/groups");
    if (!groups || groups.length === 0) {
      el.innerHTML = `
        <p style="color:var(--muted);font-size:.85rem">${t("app.no_correspondences")}</p>
        <a href="#new" class="new-corr">+ ${t("app.new_correspondence")}</a>`;
      return;
    }
    sortGroups(groups);
    const activeName =
      groups.find((g: any) => g.group_id === state.activeGroupId)?.name ?? t("app.correspondences");
    const anyTurn = groups.some((g: any) => g.is_my_turn);
    // Open by default on desktop (static list); closed on mobile so it's a
    // tap-to-expand dropdown that doesn't eat vertical space. The summary shows
    // the active group on mobile.
    const openByDefault = window.innerWidth > 640 ? "open" : "";
    el.innerHTML = `
      <details class="groups-disclosure" ${openByDefault}>
        <summary>
          <span class="sidebar-header">${t("app.correspondences")}</span>
          <span class="groups-summary-active">${escapeHtml(activeName)}${anyTurn ? " ✍️" : ""}</span>
        </summary>
        <ul class="group-list">
          ${groups.map((g: any) => `
            <li class="group-card${g.group_id === state.activeGroupId ? " active" : ""}${g.is_my_turn ? " your-turn" : ""}"
                data-gid="${g.group_id}">
              <div class="name">${escapeHtml(g.name)}</div>
              <div class="meta">${metaLabel(g)}${g.is_my_turn ? ` <span class="turn-pill">${t("app.your_turn")}</span>` : ""}</div>
            </li>`).join("")}
        </ul>
        <a href="#new" class="new-corr${state.page === "new" ? " active" : ""}">+ ${t("app.new_correspondence")}</a>
      </details>`;

    el.querySelectorAll(".group-card").forEach((card) => {
      card.addEventListener("click", () => {
        // Drive everything through the hash (the hashchange listener routes,
        // re-highlights the sidebar, and updates activeGroupId). This keeps the
        // URL in sync so refresh/deep-link restores the group.
        const gid = (card as HTMLElement).dataset.gid!;
        const g = groups.find((x: any) => x.group_id === gid);
        // A setup-status group the user founded opens its wizard, not a feed.
        const next =
          g?.status === "setup" && g?.is_founder ? `#setup/${gid}` : feedHash(gid);
        if (window.location.hash === next) routeFromHash(state); // same hash: no event
        else window.location.hash = next;
      });
    });
  } catch {
    el.innerHTML = `<p style="color:var(--muted)">${t("app.load_failed")}</p>`;
  }
}

function showLanding(): void {
  // Logged out → landing page (what snail is + how to get an invite) with the
  // login form embedded. Hide only the sidebar — #main lives inside #layout,
  // so hiding the whole layout would also hide the landing in #main.
  document.getElementById("sidebar")!.style.display = "none";
  document.getElementById("nav")!.innerHTML = "";
  renderLanding(
    document.getElementById("main")!,
    async () => {
      toast(t("app.connected"));
      await maybeRenderEmailAsk(document.getElementById("main")!, () => {
        window.location.reload();
      });
    }
  );
}

/** Couldn't reach Telegram but the session is still valid — offer a retry
 *  instead of forcing a needless re-login. */
function showReconnect(): void {
  document.getElementById("sidebar")!.style.display = "none";
  document.getElementById("nav")!.innerHTML = "";
  document.getElementById("main")!.innerHTML = `
    <div class="auth-screen">
      <h2>${t("app.reconnect.title")}</h2>
      <p style="color:var(--muted)">${t("app.reconnect.body")}</p>
      <button class="btn" id="reconnect-btn" style="margin-top:1rem">${t("app.reconnect.retry")}</button>
    </div>`;
  document.getElementById("reconnect-btn")
    ?.addEventListener("click", () => window.location.reload());
}

/** Shown when another tab already holds the single live connection. We don't
 *  connect a second MTProto client here — concurrent clients on the same auth
 *  key trip Telegram's duplicate-key revocation, which logs BOTH tabs out. */
function showAnotherTab(): void {
  document.getElementById("sidebar")!.style.display = "none";
  document.getElementById("nav")!.innerHTML = "";
  document.getElementById("main")!.innerHTML = `
    <div class="auth-screen">
      <h2>${t("app.othertab.title")}</h2>
      <p style="color:var(--muted)">${t("app.othertab.body")}</p>
      <button class="btn" id="othertab-btn" style="margin-top:1rem">${t("app.othertab.use_here")}</button>
    </div>`;
  // Reload to re-race for the lock: works once the other tab is closed; if it's
  // still open, this tab simply lands back here.
  document.getElementById("othertab-btn")
    ?.addEventListener("click", () => window.location.reload());
}

/** A held connection lease. `release()` lets a waiting tab take over before this
 *  page is even closed (used when we acquired the lease but then failed to reach
 *  Telegram — holding it while not connected would needlessly block other tabs). */
interface Lease {
  held: boolean;
  release: () => void;
}

/** Acquire the single-connection lease for this origin, or report it's taken.
 *  Web Locks are per-origin across all tabs/windows. We grab the lock and HOLD
 *  it for the lifetime of the page (the lock promise resolves only when we call
 *  release), so a second tab's `ifAvailable` request fails fast → it shows the
 *  other-tab notice instead of opening a competing MTProto connection. Releases
 *  automatically when this tab is closed, or eagerly via release().
 *  `held` is true if we hold the lease (safe to connect), false otherwise.
 *  Browsers without the Web Locks API just connect (previous behaviour). */
function acquireConnectionLease(): Promise<Lease> {
  if (!("locks" in navigator)) {
    return Promise.resolve({ held: true, release: () => {} });
  }
  return new Promise<Lease>((resolve) => {
    let releaseLock: () => void = () => {};
    navigator.locks.request(
      "snail-mtproto",
      { ifAvailable: true },
      (lock) => {
        if (!lock) {
          resolve({ held: false, release: () => {} }); // someone else holds it
          return;                                       // resolves the request now
        }
        // Hold the lock by returning a promise that resolves only on release.
        const held = new Promise<void>((r) => { releaseLock = r; });
        resolve({ held: true, release: releaseLock });
        return held;
      }
    );
  });
}

/** Adopt auth changes made in another tab. localStorage is shared across all
 *  tabs/windows of the origin, and `storage` fires in the *other* tabs when one
 *  of them writes. If a sibling tab logs in (session/token appears) or logs out
 *  (they disappear), reload so this tab reflects the same auth state instead of
 *  sitting on a stale login screen or a dead session — and crucially without
 *  spinning up a second, competing login. */
function syncAuthAcrossTabs(): void {
  window.addEventListener("storage", (e) => {
    if (e.key !== "snail_session" && e.key !== "snail_token") return;
    // React only to login (null → value) and logout (value → null), NOT to a
    // value-to-value change. restoreSession() re-saves the session whenever
    // GramJS rotates the server salt on connect; treating that drift as an auth
    // change would bounce sibling tabs into a reload that itself rotates+writes
    // the salt again — an endless cross-tab reload loop.
    const added = e.oldValue === null && e.newValue !== null;
    const removed = e.oldValue !== null && e.newValue === null;
    if (added || removed) window.location.reload();
  });
}

async function init() {
  syncAuthAcrossTabs();

  // Only one tab may hold the live MTProto connection (see acquireConnectionLease).
  // Any tab that would connect a client — whether restoring a saved session OR
  // logging in fresh — must hold the lease first; a second client on the same
  // auth key trips Telegram's duplicate-key revocation, logging BOTH tabs out.
  // So if another tab holds the lease we stop here regardless of login state:
  // the landing page's own login() connects, so it can't be reached either.
  const lease = await acquireConnectionLease();
  if (!lease.held) {
    showAnotherTab();
    return;
  }

  const session = await restoreSession();
  if (session.status === "unreachable") {
    // Valid session, but Telegram is unreachable right now — offer retry rather
    // than forcing a needless re-login (the original bug: a transient connect
    // failure bounced signed-in users to the sign-in screen). Release the lease:
    // we're not actually connected, so another tab should be free to take over.
    lease.release();
    showReconnect();
    return;
  }
  const route = pathRoute();
  if (session.status === "client" && !getToken()) {
    // Telegram session is valid but the API token is missing or was cleared
    // after expiry (apiFetch 401 → clearToken → reload lands here). Re-mint
    // silently through the bot handshake instead of bouncing the user to the
    // login screen — the session IS the identity; no code re-entry needed.
    try {
      await refreshToken(session.client);
    } catch (e) {
      console.error("[init] token re-mint failed:", e);
    }
  }
  if (session.status !== "client" || !getToken()) {
    // No session, a revoked session, or no API token → genuine logged-out.
    // An invitee landing on /join/<token> gets the join page with its own
    // embedded sign-in (kinder than the generic landing); everyone else —
    // including a new founder on /setup/<token>, whose path survives the
    // post-login reload — gets the landing, which re-auths and re-mints.
    if (route?.kind === "join") {
      document.getElementById("sidebar")!.style.display = "none";
      document.getElementById("nav")!.innerHTML = "";
      renderJoinPage(document.getElementById("main")!, null, route.token);
      return;
    }
    showLanding();
    return;
  }
  const client = session.client;

  // Path entries (join / new-founder claim) are standalone pages: no sidebar,
  // no group routing. The wizard's own flow returns to the app when done.
  if (route) {
    document.getElementById("sidebar")!.style.display = "none";
    document.getElementById("nav")!.innerHTML = "";
    const main = document.getElementById("main")!;
    if (route.kind === "join") renderJoinPage(main, client, route.token);
    else await renderSetupClaim(main, client, route.token);
    return;
  }

  const me = await client.getMe();

  // Pre-warm GramJS's entity cache once (a single getDialogs) so a cold feed's
  // resolveChat usually hits getInputEntity directly instead of falling back to
  // its own reactive getDialogs round-trip per thread. Fire-and-forget — must
  // not delay first paint.
  void client.getDialogs({ limit: 100 }).catch(() => {});

  const state: AppState = {
    client,
    me: { id: Number(me.id), username: (me as any).username },
    page: "feed",
    activeGroupId: null,
    defaultGroupId: null,
    botUsername: botUsername(),
  };
  (window as any).__app = state;

  // Fetch groups once to know the default group and whether this is a new
  // member (no groups → show About).
  let firstRun = false;
  let firstGroupId: string | null = null;
  try {
    const { groups } = await apiFetch("/groups");
    firstRun = (groups?.length ?? 0) === 0;
    firstGroupId = groups?.[0]?.group_id ?? null;
  } catch { /* fall through */ }
  state.defaultGroupId = firstGroupId;

  if (firstRun && !window.location.hash) {
    // No groups AND no explicit destination → orientation. A founder resuming
    // their wizard (#setup/<gid>) has zero groups too; the hash must win.
    await routeMain(state, "about");
  } else {
    await routeFromHash(state);
  }
  window.addEventListener("hashchange", () => routeFromHash(state));
}

/** Resolve the current hash to a page + group, sync activeGroupId and the
 *  sidebar highlight, and render. The one place navigation is applied. */
async function routeFromHash(state: AppState): Promise<void> {
  const { page, groupId } = parseHash();
  // A feed/settings route needs a group; keep the open one, else the default.
  state.activeGroupId = resolveActiveGroup(groupId, state.activeGroupId, state.defaultGroupId);
  // Paint the destination first — the sidebar refresh is a network fetch
  // (/groups) and must not delay navigation; it re-highlights when it lands.
  const sidebar = loadSidebar(state);
  await routeMain(state, page);
  await sidebar;
}

init();
